/**
 * Sticky tester-preview memory: one browser remembers a preview link it was
 * handed, so the preview survives every in-app link that forgets to carry its
 * query param.
 *
 * WHY. The tester doors (`?v1Preview=1`, `?giftGuideHub=1`) used to live ONLY
 * in the URL, so every in-app hop had to re-attach them (`withV1PreviewParam`,
 * the hub carriers). Any hop that didn't — the People-page recipient tile's bare
 * `/board/<id>`, verified live 2026-10-14 — silently dropped the tester back to
 * today's UI, which reads exactly like "the feature isn't working". Chasing
 * carriers one link at a time kept breaking; remembering the door does not.
 *
 *   visit any page with ?v1Preview=1       -> remember V1 preview ON
 *   visit any page with ?giftGuideHub=1    -> remember hub preview ON
 *     (or its alias ?freshFindFridays=1)
 *   ?v1Preview=0 / ?giftGuideHub=0 -> turn it OFF and forget it
 *     (or ?freshFindFridays=0)
 *
 * THE OVERRIDE LAYER, NOT THE FEATURES. `hasV1PreviewOverride` and
 * `hasGiftGuideHubOverride` resolve through `resolvePreviewOverride` below, so
 * every per-feature gate that already ORs one of them in inherits the memory
 * with no edit. An explicit URL value always wins over memory, in both
 * directions, so the `=0` page itself already renders preview-off.
 *
 * WRITES HAPPEN ONCE, AT BOOT (`initPreviewMemory`, called from index.js), not
 * inside the readers. The readers are called during render, many times per
 * render, sometimes on a search string that is not the current location; a
 * reader that also wrote storage would be a side effect hiding in a predicate.
 * Boot is also early enough: it runs before React mounts, so nothing (a lazy
 * route chunk, a `<Navigate replace>`) can strip the param first — the
 * sessionStorage/lazy-chunk trap in gotchas-web-frontend.md. A tester only
 * ever arrives at a door param by loading a URL (pasted link, address bar), and
 * the first render on that page reads the param from the URL directly anyway,
 * so there is no flash between the visit and the write.
 *
 * CARRIERS NOW STAMP FROM MEMORY TOO. `withV1PreviewParam`, the hub carriers
 * and the per-feature ones key on these same readers, so a remembered tester's
 * in-app links keep carrying the params, exactly as a URL-carried tester's did
 * before. Left that way on purpose (the carriers stay unchanged); the cost is
 * that an address-bar URL a tester shares enrolls its recipient's browser, and
 * an old tab or bookmark carrying `=1` re-enrols after `=0`. `=0` is the exit.
 *
 * localStorage, not sessionStorage: a flag a human browses behind must survive
 * a new tab and a browser restart (same gotcha). Every storage touch is wrapped
 * — private mode, blocked site data or a quota error degrade to today's
 * URL-only behaviour and never throw into a render. Real visitors who never
 * open a tester link have no key, so their gates resolve exactly as before.
 */

/** Namespaced localStorage keys. A key holds `'1'` while the preview is on and
 *  is removed when it is turned off; any other value reads as off. */
export const V1_PREVIEW_MEMORY_KEY = 'thea:preview:v1Preview';
export const GIFT_GUIDE_HUB_MEMORY_KEY = 'thea:preview:giftGuideHub';

const ON = '1';
const OFF = '0';

/**
 * What the URL explicitly says about one preview param: `true` for `=1`,
 * `false` for `=0`, `null` for absent or any other value (which defers to
 * memory rather than switching anything).
 */
export function previewParamValue(search: string, param: string): boolean | null {
  if (!search) return null;
  try {
    const value = new URLSearchParams(search).get(param);
    if (value === ON) return true;
    if (value === OFF) return false;
    return null;
  } catch {
    // A malformed query string is not a reason to crash a render.
    return null;
  }
}

/** Is this preview remembered ON in this browser? False when storage is
 *  unavailable — the fail-closed answer. */
export function readPreviewMemory(key: string): boolean {
  try {
    return window.localStorage.getItem(key) === ON;
  } catch {
    return false;
  }
}

/** Remember (`on`) or forget (`!on`) a preview. Silent no-op when storage is
 *  unavailable. */
export function writePreviewMemory(key: string, on: boolean): void {
  try {
    if (on) window.localStorage.setItem(key, ON);
    else window.localStorage.removeItem(key);
  } catch {
    /* private mode / blocked storage / quota — URL-only behaviour remains. */
  }
}

/**
 * The override reader every tester door resolves through: the URL's explicit
 * `=1` / `=0` if present, otherwise this browser's memory. Synchronous and
 * read-only, so a tester-link page reads `true` on its very first render.
 */
export function resolvePreviewOverride(
  search: string,
  param: string,
  key: string,
): boolean {
  const explicit = previewParamValue(search, param);
  if (explicit !== null) return explicit;
  return readPreviewMemory(key);
}
